import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import { useWizardContext } from "../WizardContext"
import { buildDetailsPayload } from "../mappers/toApiPayload"
import { updateTrip } from "store/trips"
import { ExperienceInfo } from "../components/details/ExperienceInfo"
import { ExperienceDetails } from "../components/details/ExperienceDetails"
import { AdditionalDetails } from "../components/details/AdditionalDetails"
import { PublishingSettings } from "../components/details/PublishingSettings"

/**
 * Details step — title, description, categories, guests, languages, skills, toggles.
 *
 * Uses the shared form instance from WizardContext (no wrapping <Form>).
 * ExperienceDetails reports language/skill changes via prop callbacks (not Form.Item onChange),
 * so we track them in local state and include them in the registered save handler.
 */
export const DetailsStep = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const { form, stepSaveHandlersRef, trip } = useWizardContext()

  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [selectedSkillLevels, setSelectedSkillLevels] = useState([])

  // Initialize from form values (which have defaults applied by buildDefaultValues)
  useEffect(() => {
    const formLanguages = form.getFieldValue("languages")
    const formSkillLevels = form.getFieldValue("skill_levels")
    
    if (formLanguages?.length) {
      setSelectedLanguages(formLanguages)
    }
    if (formSkillLevels?.length) {
      setSelectedSkillLevels(formSkillLevels)
    }
  }, [trip?.id, form]) // eslint-disable-line react-hooks/exhaustive-deps

  // Register save handler — closure captures latest local state
  useEffect(() => {
    stepSaveHandlersRef.current["details"] = async () => {
      await form.validateFields(["title", "description"])
      const formValues = form.getFieldsValue()
      const payload = buildDetailsPayload(formValues, { selectedLanguages, selectedSkillLevels })
      await dispatch(updateTrip(id, payload))
    }
    return () => {
      delete stepSaveHandlersRef.current["details"]
    }
  }, [dispatch, id, form, stepSaveHandlersRef, selectedLanguages, selectedSkillLevels])

  return (
    <div style={{ maxWidth: "984px" }}>
      <ExperienceInfo form={form} />
      <ExperienceDetails
        form={form}
        setSelectedLanguages={setSelectedLanguages}
        selectedLanguages={selectedLanguages}
        selectedSkillLevels={selectedSkillLevels}
        setSelectedSkillLevels={setSelectedSkillLevels}
        onLanguageInteraction={() => {}}
        onSkillInteraction={() => {}}
      />
      <AdditionalDetails form={form} />
      <PublishingSettings form={form} />
    </div>
  )
}
